import { useEffect, useMemo, useState } from 'react';
import { Archive, CheckCircle2, ChevronLeft, ChevronRight, FileSpreadsheet, FileText, Filter, History, Info, Lightbulb, Loader2, Plus, RotateCcw, Save, Search, Target, Workflow, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { getAllClients, type ClientItem } from '../services/clientService';
import { getPartiesPrenantes, type PartiePrenante } from '../services/partiesPrenantesService';
import {
  createClientOnboarding,
  createTemplateItems,
  deleteClientOnboarding,
  getClientOnboarding,
  listClientOnboardings,
  onboardingPriorities,
  onboardingStatuses,
  restoreClientOnboarding,
  updateClientOnboarding,
  type ClientOnboarding,
  type ClientOnboardingItem,
} from '../services/clientOnboardingService';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type PriseEnChargeTab = 'registre' | 'fonctionnement';

const emptyOnboarding = (): ClientOnboarding => ({
  client_id: '',
  technicien_id: '',
  titre: 'Prise en charge client TI',
  statut: 'À faire',
  priorite: 'Moyenne',
  urgence_ti: '',
  site_principal: '',
  contact_principal: '',
  periode_couverte: '',
  prepare_pour: '',
  avancement_global: '',
  risques_critiques: '',
  actions_ouvertes: '',
  decisions_requises: '',
  prochaine_rencontre: '',
  compte_rendu: {
    resume_executif: '',
    points_valides: '',
    points_a_corriger: '',
    recommandations_prioritaires: '',
    prochaines_etapes: '',
  },
});

function formatDate(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('fr-CA', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function progressPercent(onboarding: ClientOnboarding) {
  const total = Number(onboarding.total_items || onboarding.items?.length || 0);
  const done = Number(onboarding.completed_items || onboarding.items?.filter((item) => item.checked).length || 0);
  if (!total) return 0;
  return Math.round((done / total) * 100);
}

function safeFileName(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80)
    .toLowerCase();
}

function asText(value: unknown) {
  return value === null || value === undefined ? '' : String(value);
}

function historyEventLabel(eventType: string) {
  if (eventType === 'created') return 'Création';
  if (eventType === 'deleted') return 'Archivage';
  if (eventType === 'restored') return 'Restauration';
  return 'Mise à jour';
}

export default function PriseEnChargeClientModal({ isOpen, onClose }: Props) {
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [techniciens, setTechniciens] = useState<PartiePrenante[]>([]);
  const [onboardings, setOnboardings] = useState<ClientOnboarding[]>([]);
  const [selected, setSelected] = useState<ClientOnboarding>(emptyOnboarding());
  const [items, setItems] = useState<ClientOnboardingItem[]>(createTemplateItems());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterTechnicien, setFilterTechnicien] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [viewMode, setViewMode] = useState<'active' | 'archives'>('active');
  const [activeTab, setActiveTab] = useState<PriseEnChargeTab>('registre');
  const [activeSection, setActiveSection] = useState('informations-generales');
  const [clientInfoQuestionIndex, setClientInfoQuestionIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    let ignore = false;
    setLoading(true);
    setError('');
    Promise.all([getAllClients(), getPartiesPrenantes(2), listClientOnboardings({ archived: viewMode === 'archives' })])
      .then(([clientRows, techRows, onboardingRows]) => {
        if (ignore) return;
        setClients(clientRows.filter((client) => !client.archived));
        setTechniciens(techRows.filter((pp) => pp.actif !== false));
        setOnboardings(onboardingRows);
      })
      .catch((err) => {
        if (!ignore) setError(err instanceof Error ? err.message : 'Erreur chargement prise en charge');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timeout = window.setTimeout(() => {
      void refreshList(viewMode);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [search, filterClient, filterTechnicien, filterStatut, viewMode, isOpen]);

  const sections = useMemo(() => {
    const map = new Map<string, { key: string; title: string; items: ClientOnboardingItem[] }>();
    items.forEach((item) => {
      if (!map.has(item.section_key)) {
        map.set(item.section_key, { key: item.section_key, title: item.section_title, items: [] });
      }
      map.get(item.section_key)?.items.push(item);
    });
    return Array.from(map.values());
  }, [items]);

  const activeItems = sections.find((section) => section.key === activeSection)?.items || [];
  const isClientInfoSection = true;
  const activeClientInfoItem = activeItems[clientInfoQuestionIndex];
  const selectedProgress = progressPercent(selected);
  const isArchivedView = viewMode === 'archives' || Boolean(selected.archived);

  useEffect(() => {
    setClientInfoQuestionIndex((previous) => Math.min(previous, Math.max(activeItems.length - 1, 0)));
  }, [activeItems.length]);

  useEffect(() => {
    setClientInfoQuestionIndex(0);
  }, [activeSection]);

  if (!isOpen) return null;

  async function refreshList(mode: 'active' | 'archives' = viewMode) {
    try {
      const rows = await listClientOnboardings({
        search,
        clientId: filterClient,
        technicienId: filterTechnicien,
        statut: filterStatut,
        archived: mode === 'archives',
      });
      setOnboardings(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur chargement historique');
    }
  }

  async function openOnboarding(id?: number) {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const full = await getClientOnboarding(id);
      setSelected(full);
      setItems(full.items?.length ? full.items : createTemplateItems());
      setActiveSection(full.items?.[0]?.section_key || 'informations-generales');
      setClientInfoQuestionIndex(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur ouverture prise en charge');
    } finally {
      setLoading(false);
    }
  }

  function startNew() {
    setViewMode('active');
    setSelected(emptyOnboarding());
    setItems(createTemplateItems());
    setActiveSection('informations-generales');
    setClientInfoQuestionIndex(0);
    setError('');
  }

  function updateSelected<K extends keyof ClientOnboarding>(key: K, value: ClientOnboarding[K]) {
    setSelected((prev) => ({ ...prev, [key]: value }));
  }

  function updateReportField(key: keyof NonNullable<ClientOnboarding['compte_rendu']>, value: string) {
    setSelected((prev) => ({
      ...prev,
      compte_rendu: {
        ...(prev.compte_rendu || {}),
        [key]: value,
      },
    }));
  }

  function updateItem(item: ClientOnboardingItem, updates: Partial<ClientOnboardingItem>) {
    setItems((prev) =>
      prev.map((row) =>
        row.section_key === item.section_key && row.item_order === item.item_order && row.element === item.element
          ? { ...row, ...updates }
          : row,
      ),
    );
  }

  function getExportName(extension: 'xlsx' | 'pdf') {
    const client = clients.find((row) => String(row.id) === String(selected.client_id));
    const base = safeFileName(`prise_en_charge_${client?.nom || selected.client_nom || 'client'}_${new Date().toISOString().slice(0, 10)}`);
    return `${base}.${extension}`;
  }

  function exportExcel() {
    const wb = XLSX.utils.book_new();
    const client = clients.find((row) => String(row.id) === String(selected.client_id));
    const tech = techniciens.find((row) => String(row.id) === String(selected.technicien_id));

    const summaryRows = [
      ['Champ', 'Valeur'],
      ['Client', client?.nom || selected.client_nom || ''],
      ['Technicien Prime', [tech?.prenom, tech?.nom].filter(Boolean).join(' ') || selected.technicien_nom || ''],
      ['Titre', selected.titre],
      ['Statut', selected.statut],
      ['Priorité', selected.priorite],
      ['Urgence TI', selected.urgence_ti || ''],
      ['Site principal', selected.site_principal || ''],
      ['Contact principal', selected.contact_principal || ''],
      ['Période couverte', selected.periode_couverte || ''],
      ['Préparé pour', selected.prepare_pour || ''],
      ['Avancement', `${selectedProgress}%`],
      ['Créé le', selected.created_at ? formatDate(selected.created_at) : ''],
      ['Dernière mise à jour', selected.updated_at ? formatDate(selected.updated_at) : ''],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Résumé');

    const checklistRows = [
      ['Section', 'Fait', 'Priorité', 'Statut', 'Élément', 'Notes / preuves', 'Action', 'Responsable', 'Échéance'],
      ...items.map((item) => [
        item.section_title,
        item.checked ? 'Oui' : 'Non',
        item.priorite || '',
        item.statut || '',
        item.element,
        item.notes || '',
        item.action || '',
        item.responsable || '',
        item.echeance || '',
      ]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(checklistRows), 'Checklist');

    const report = selected.compte_rendu || {};
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([
        ['Section', 'Contenu'],
        ['Résumé exécutif', report.resume_executif || ''],
        ['Points validés', report.points_valides || ''],
        ['Points à corriger', report.points_a_corriger || ''],
        ['Recommandations prioritaires', report.recommandations_prioritaires || ''],
        ['Prochaines étapes', report.prochaines_etapes || ''],
      ]),
      'Compte rendu',
    );

    const historyRows = [
      ['Événement', 'Date', 'Utilisateur'],
      ...(selected.history || []).map((event) => [
        historyEventLabel(event.event_type),
        formatDate(event.created_at),
        event.actor_email || '',
      ]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(historyRows), 'Historique');
    XLSX.writeFile(wb, getExportName('xlsx'));
  }

  function exportPdf() {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 16;
    const maxWidth = pageWidth - margin * 2;
    const client = clients.find((row) => String(row.id) === String(selected.client_id));
    const tech = techniciens.find((row) => String(row.id) === String(selected.technicien_id));
    const clientName = client?.nom || selected.client_nom || 'Client PRIsME';
    const techName = [tech?.prenom, tech?.nom].filter(Boolean).join(' ') || selected.technicien_nom || 'À confirmer';
    let y = 22;

    const addFooter = () => {
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`PRIsME BZ - Prise en charge client TI - Page ${i}/${pageCount}`, margin, pageHeight - 8);
      }
    };

    const ensureSpace = (height: number) => {
      if (y + height > pageHeight - 18) {
        doc.addPage();
        y = 18;
      }
    };

    const sectionTitle = (title: string) => {
      ensureSpace(12);
      doc.setFillColor(15, 23, 42);
      doc.rect(margin, y, maxWidth, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255);
      doc.text(title, margin + 3, y + 5.5);
      y += 12;
      doc.setTextColor(30);
    };

    const paragraph = (text: string, fontSize = 9) => {
      const clean = text.trim() || '-';
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);
      doc.setTextColor(45);
      const lines = doc.splitTextToSize(clean, maxWidth);
      ensureSpace(lines.length * 5 + 2);
      doc.text(lines, margin, y);
      y += lines.length * 5 + 4;
    };

    doc.setFillColor(10, 56, 86);
    doc.rect(0, 0, pageWidth, 44, 'F');
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('Prise en charge client TI', margin, 20);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Rapport professionnel - Techs Primes BZ', margin, 29);
    doc.text(new Intl.DateTimeFormat('fr-CA', { dateStyle: 'long' }).format(new Date()), margin, 37);

    y = 56;
    sectionTitle('Sommaire');
    const summary = [
      ['Client', clientName],
      ['Technicien Prime', techName],
      ['Statut', selected.statut],
      ['Priorité', selected.priorite],
      ['Urgence TI', selected.urgence_ti || '-'],
      ['Site principal', selected.site_principal || '-'],
      ['Contact principal', selected.contact_principal || '-'],
      ['Période couverte', selected.periode_couverte || '-'],
      ['Avancement global', `${selectedProgress}% (${items.filter((item) => item.checked).length}/${items.length})`],
    ];
    doc.setFontSize(9);
    summary.forEach(([label, value]) => {
      ensureSpace(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(`${label}:`, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(45);
      doc.text(doc.splitTextToSize(asText(value), maxWidth - 48), margin + 48, y);
      y += 7;
    });

    const report = selected.compte_rendu || {};
    sectionTitle('Compte rendu');
    [
      ['Résumé exécutif', report.resume_executif],
      ['Points validés', report.points_valides],
      ['Points à corriger', report.points_a_corriger],
      ['Recommandations prioritaires', report.recommandations_prioritaires],
      ['Prochaines étapes', report.prochaines_etapes],
    ].forEach(([label, text]) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      ensureSpace(7);
      doc.text(asText(label), margin, y);
      y += 5;
      paragraph(asText(text), 9);
    });

    sectionTitle('Checklist terrain');
    sections.forEach((section) => {
      ensureSpace(10);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(10, 56, 86);
      doc.text(section.title, margin, y);
      y += 6;
      section.items.forEach((item) => {
        const marker = item.checked ? '[x]' : '[ ]';
        const line = `${marker} ${item.element} - ${item.statut || 'À faire'}${item.priorite ? ` / ${item.priorite}` : ''}`;
        const detail = [item.notes ? `Notes: ${item.notes}` : '', item.action ? `Action: ${item.action}` : '', item.responsable ? `Responsable: ${item.responsable}` : '', item.echeance ? `Échéance: ${item.echeance}` : '']
          .filter(Boolean)
          .join(' | ');
        ensureSpace(detail ? 14 : 7);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(45);
        doc.text(doc.splitTextToSize(line, maxWidth), margin + 2, y);
        y += 5;
        if (detail) {
          doc.setTextColor(90);
          const lines = doc.splitTextToSize(detail, maxWidth - 6);
          doc.text(lines, margin + 6, y);
          y += lines.length * 4 + 2;
        }
      });
      y += 2;
    });

    if (selected.history?.length) {
      sectionTitle('Historique');
      selected.history.slice(0, 20).forEach((event) => {
        ensureSpace(7);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(45);
        const eventLabel = historyEventLabel(event.event_type);
        doc.text(`${eventLabel} - ${formatDate(event.created_at)}${event.actor_email ? ` - ${event.actor_email}` : ''}`, margin, y);
        y += 5;
      });
    }

    addFooter();
    doc.save(getExportName('pdf'));
  }

  async function handleSave() {
    if (selected.archived) {
      setError('Restaure la prise en charge avant de la modifier.');
      return;
    }
    if (!selected.client_id) {
      setError('Choisis un client PRIsME avant de sauvegarder.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: ClientOnboarding = {
        ...selected,
        client_id: Number(selected.client_id),
        technicien_id: selected.technicien_id ? Number(selected.technicien_id) : undefined,
      };
      const saved = selected.id
        ? await updateClientOnboarding(selected.id, payload, items)
        : await createClientOnboarding(payload, items);
      setSelected(saved);
      setItems(saved.items?.length ? saved.items : items);
      await refreshList('active');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur sauvegarde prise en charge');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selected.id) return;
    const confirmed = window.confirm('Archiver cette prise en charge?');
    if (!confirmed) return;
    setSaving(true);
    setError('');
    try {
      await deleteClientOnboarding(selected.id);
      setSelected(emptyOnboarding());
      setItems(createTemplateItems());
      setActiveSection('informations-generales');
      setViewMode('archives');
      await refreshList('archives');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur archivage');
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore() {
    if (!selected.id) return;
    setSaving(true);
    setError('');
    try {
      const restored = await restoreClientOnboarding(selected.id);
      setViewMode('active');
      setSelected(restored);
      setItems(restored.items?.length ? restored.items : createTemplateItems());
      await refreshList('active');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur restauration');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm p-2 lg:p-3">
      <div className="mx-auto flex h-full max-w-[98vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/20">
        <header className="relative flex items-center justify-between overflow-hidden border-b border-slate-900 px-5 py-4">
          <div className="absolute inset-0 bg-slate-950" />
          <div className="absolute inset-y-0 left-0 w-2/3 bg-linear-to-r from-blue-800 via-cyan-700 to-emerald-600 opacity-90" />
          <div className="absolute inset-y-0 right-0 w-1/2 bg-linear-to-l from-slate-950 via-slate-950/95 to-transparent" />
          <div className="relative">
            <div className="mb-1 inline-flex items-center rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-cyan-50">
              Techs Primes · Checklist terrain
            </div>
            <h2 className="text-2xl font-extrabold text-white">Prise en charge client TI</h2>
            <p className="text-xs font-medium text-cyan-50">Checklist terrain liée aux clients PRIsME, avec suivi, preuves, actions et historique.</p>
          </div>
          <div className="relative flex items-center gap-2">
            <button
              type="button"
              onClick={exportExcel}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-emerald-200/60 bg-emerald-50 px-3 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-100"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Excel
            </button>
            <button
              type="button"
              onClick={exportPdf}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/40 bg-white/95 px-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-white"
            >
              <FileText className="h-4 w-4" />
              PDF
            </button>
            <button
              type="button"
              onClick={startNew}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/40 bg-white/95 px-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-white"
            >
              <Plus className="h-4 w-4" />
              Nouvelle
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || isArchivedView}
              title={isArchivedView ? 'Restaure la prise en charge avant de la modifier.' : undefined}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-500 px-3 text-sm font-semibold text-white shadow-sm shadow-blue-950/30 hover:bg-blue-400 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Sauvegarder
            </button>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-white/75 hover:bg-white/10 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        {error && <div className="border-b border-red-100 bg-red-50 px-5 py-2 text-sm text-red-700">{error}</div>}
        {isArchivedView && selected.id && (
          <div className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-sm font-medium text-amber-800">
            Cette prise en charge est archivée. Elle peut être consultée et exportée, mais doit être restaurée avant modification.
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-b border-slate-300 bg-white px-5 py-3">
          <button
            type="button"
            onClick={() => setActiveTab('registre')}
            className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold ${
              activeTab === 'registre' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <CheckCircle2 className="h-4 w-4" />
            Registre
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('fonctionnement')}
            className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold ${
              activeTab === 'fonctionnement' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Info className="h-4 w-4" />
            Fonctionnement
          </button>
        </div>

        {activeTab === 'registre' ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 bg-slate-100 lg:grid-cols-[390px_1fr]">
          <aside className="flex min-h-0 flex-col border-r border-slate-300 bg-slate-50">
            <div className="space-y-2 border-b border-slate-300 bg-white p-3">
              <div className="grid grid-cols-2 rounded-xl border border-slate-300 bg-slate-100 p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('active');
                    setSelected(emptyOnboarding());
                    setItems(createTemplateItems());
                  }}
                  className={`h-8 rounded-lg text-xs font-semibold transition ${viewMode === 'active' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-cyan-50'}`}
                >
                  Actives
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('archives');
                    setSelected(emptyOnboarding());
                    setItems(createTemplateItems());
                  }}
                  className={`h-8 rounded-lg text-xs font-semibold transition ${viewMode === 'archives' ? 'bg-amber-600 text-white shadow-sm' : 'text-slate-600 hover:bg-amber-50'}`}
                >
                  Archives
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Client, titre, technicien..."
                  className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-8 pr-3 text-sm text-slate-900 outline-none shadow-sm placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-2 text-xs text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  <option value="">Tous clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nom}
                    </option>
                  ))}
                </select>
                <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-2 text-xs text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  <option value="">Tous statuts</option>
                  {onboardingStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <select value={filterTechnicien} onChange={(e) => setFilterTechnicien(e.target.value)} className="h-10 w-full rounded-xl border border-slate-300 bg-white px-2 text-xs text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                <option value="">Tous les techniciens</option>
                {techniciens.map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {[tech.prenom, tech.nom].filter(Boolean).join(' ') || tech.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {loading && (
                <div className="flex items-center gap-2 rounded-lg bg-white p-3 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement...
                </div>
              )}
              {!loading && onboardings.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                  {viewMode === 'archives' ? 'Aucune prise en charge archivée.' : 'Aucune prise en charge active trouvée.'}
                </div>
              )}
              <div className="space-y-2">
                {onboardings.map((row) => (
                  <button
                    type="button"
                    key={row.id}
                    onClick={() => void openOnboarding(row.id)}
                    className={`w-full rounded-2xl border p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                      selected.id === row.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-300 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-slate-900">{row.client_nom || row.titre}</div>
                        <div className="mt-0.5 text-xs text-slate-500">{row.technicien_nom || 'Technicien à choisir'}</div>
                      </div>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${row.archived ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {row.archived ? 'Archivée' : row.statut}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-slate-100">
                      <div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${progressPercent(row)}%` }} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                      <span>{row.completed_items || 0}/{row.total_items || 0} vérifications</span>
                      <span>{formatDate(row.updated_at)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main className="min-h-0 overflow-y-auto p-5">
            <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-4">
              <label className="text-xs font-semibold text-slate-600">
                Client PRIsME
                <select value={selected.client_id} onChange={(e) => updateSelected('client_id', e.target.value ? Number(e.target.value) : '')} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  <option value="">Choisir...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nom}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Technicien Prime
                <select value={selected.technicien_id || ''} onChange={(e) => updateSelected('technicien_id', e.target.value ? Number(e.target.value) : '')} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  <option value="">Choisir...</option>
                  {techniciens.map((tech) => (
                    <option key={tech.id} value={tech.id}>
                      {[tech.prenom, tech.nom].filter(Boolean).join(' ') || tech.email}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Statut
                <select value={selected.statut} onChange={(e) => updateSelected('statut', e.target.value as ClientOnboarding['statut'])} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  {onboardingStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Priorité
                <select value={selected.priorite} onChange={(e) => updateSelected('priorite', e.target.value as ClientOnboarding['priorite'])} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  {onboardingPriorities.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-5">
              {[
                ['site_principal', 'Site principal'],
                ['contact_principal', 'Contact principal'],
                ['urgence_ti', 'Urgence TI'],
                ['periode_couverte', 'Période couverte'],
                ['prepare_pour', 'Préparé pour'],
              ].map(([key, label]) => (
                <label key={key} className="text-xs font-semibold text-slate-600">
                  {label}
                  <input
                    value={String(selected[key as keyof ClientOnboarding] || '')}
                    onChange={(e) => updateSelected(key as keyof ClientOnboarding, e.target.value as never)}
                    className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                </label>
              ))}
            </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
              {[
                ['Avancement', `${selectedProgress}%`, 'from-blue-50 to-cyan-50', 'text-blue-700', 'border-blue-100'],
                ['Risques ouverts', String(items.filter((item) => !item.checked && item.priorite === 'Critique').length), 'from-rose-50 to-orange-50', 'text-rose-700', 'border-rose-100'],
                ['Actions ouvertes', String(items.filter((item) => !item.checked && item.action?.trim()).length), 'from-amber-50 to-yellow-50', 'text-amber-700', 'border-amber-100'],
                ['Éléments faits', String(items.filter((item) => item.checked).length), 'from-emerald-50 to-teal-50', 'text-emerald-700', 'border-emerald-100'],
                ['Historique', String(selected.history?.length || 0), 'from-violet-50 to-indigo-50', 'text-violet-700', 'border-violet-100'],
              ].map(([label, value, gradient, valueColor, borderColor]) => (
                <div key={label} className={`rounded-2xl border ${borderColor} bg-linear-to-br ${gradient} p-3 shadow-md shadow-slate-200/70`}>
                  <div className="text-xs font-semibold text-slate-500">{label}</div>
                  <div className={`text-2xl font-extrabold ${valueColor}`}>{value}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[270px_1fr]">
              <nav className="space-y-1.5">
                {sections.map((section) => {
                  const total = section.items.length;
                  const done = section.items.filter((item) => item.checked).length;
                  const percent = total ? Math.round((done / total) * 100) : 0;
                  return (
                    <button
                      key={section.key}
                      type="button"
                      onClick={() => setActiveSection(section.key)}
                      className={`w-full rounded-xl border px-3 py-2.5 text-left text-xs font-semibold shadow-sm transition ${
                        activeSection === section.key ? 'border-blue-500 bg-blue-50 text-blue-900 ring-2 ring-blue-200' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div>{section.title}</div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                        <div className="h-1.5 rounded-full bg-linear-to-r from-blue-500 to-emerald-500" style={{ width: `${percent}%` }} />
                      </div>
                      <div className="mt-1 text-[11px] font-normal text-slate-500">{done}/{total}</div>
                    </button>
                  );
                })}
              </nav>

              <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-300 bg-slate-100 px-4 py-3">
                  <h3 className="font-bold text-slate-900">{sections.find((section) => section.key === activeSection)?.title}</h3>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Filter className="h-4 w-4" />
                    {activeItems.filter((item) => item.checked).length}/{activeItems.length}
                  </div>
                </div>

                {isClientInfoSection ? (
                  <div className="bg-slate-50 p-4">
                    <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50 p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <div className="text-sm font-extrabold text-blue-950">Questionnaire de section</div>
                          <p className="mt-1 text-xs leading-relaxed text-blue-800">
                            Remplis une question à la fois pour alléger la prise d'information et le suivi terrain.
                          </p>
                        </div>
                        <div className="rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-extrabold text-blue-800">
                          Question {activeItems.length ? clientInfoQuestionIndex + 1 : 0}/{activeItems.length}
                        </div>
                      </div>
                      <div className="mt-4 h-2 rounded-full bg-white">
                        <div
                          className="h-2 rounded-full bg-linear-to-r from-blue-500 to-emerald-500 transition-all"
                          style={{ width: activeItems.length ? `${((clientInfoQuestionIndex + 1) / activeItems.length) * 100}%` : '0%' }}
                        />
                      </div>
                    </div>
                    {activeClientInfoItem ? (
                      <div className="mx-auto max-w-4xl">
                        <div
                          key={`${activeClientInfoItem.section_key}-${activeClientInfoItem.item_order}-${activeClientInfoItem.element}`}
                          className={`rounded-2xl border p-4 shadow-sm transition ${
                            activeClientInfoItem.checked ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <button
                              type="button"
                              onClick={() => updateItem(activeClientInfoItem, { checked: !activeClientInfoItem.checked, statut: !activeClientInfoItem.checked ? 'Fait' : 'À faire' })}
                              className={`mt-0.5 rounded-full p-1 ${activeClientInfoItem.checked ? 'text-emerald-600' : 'text-slate-300 hover:text-slate-500'}`}
                              title={activeClientInfoItem.checked ? 'Marquer à faire' : 'Marquer fait'}
                            >
                              <CheckCircle2 className="h-6 w-6" />
                            </button>
                            <div className="min-w-0 flex-1">
                              <label className="block text-lg font-extrabold text-slate-900">
                                {activeClientInfoItem.element}
                                <textarea
                                  value={activeClientInfoItem.notes || ''}
                                  onChange={(e) => updateItem(activeClientInfoItem, { notes: e.target.value, statut: e.target.value.trim() ? activeClientInfoItem.statut || 'En cours' : activeClientInfoItem.statut })}
                                  placeholder="Réponse / notes / preuves..."
                                  className="mt-3 h-48 w-full resize-y rounded-xl border border-slate-300 bg-white px-4 py-3 text-base font-medium text-slate-900 shadow-inner outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                />
                              </label>
                              <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-5">
                                <label className="text-[11px] font-semibold text-slate-500">
                                  Priorité
                                  <select
                                    value={activeClientInfoItem.priorite || ''}
                                    onChange={(e) => updateItem(activeClientInfoItem, { priorite: e.target.value as ClientOnboardingItem['priorite'] })}
                                    className="mt-1 h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                  >
                                    <option value="">-</option>
                                    {onboardingPriorities.map((priority) => (
                                      <option key={priority} value={priority}>
                                        {priority}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">
                                  Statut
                                  <select
                                    value={activeClientInfoItem.statut || ''}
                                    onChange={(e) => updateItem(activeClientInfoItem, { statut: e.target.value as ClientOnboardingItem['statut'] })}
                                    className="mt-1 h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                  >
                                    <option value="">-</option>
                                    {onboardingStatuses.map((status) => (
                                      <option key={status} value={status}>
                                        {status}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">
                                  Action / suivi
                                  <input
                                    value={activeClientInfoItem.action || ''}
                                    onChange={(e) => updateItem(activeClientInfoItem, { action: e.target.value })}
                                    placeholder="Optionnel"
                                    className="mt-1 h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                  />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">
                                  Responsable
                                  <input
                                    value={activeClientInfoItem.responsable || ''}
                                    onChange={(e) => updateItem(activeClientInfoItem, { responsable: e.target.value })}
                                    placeholder="Optionnel"
                                    className="mt-1 h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                  />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">
                                  Échéance
                                  <input
                                    type="date"
                                    value={activeClientInfoItem.echeance || ''}
                                    onChange={(e) => updateItem(activeClientInfoItem, { echeance: e.target.value || null })}
                                    className="mt-1 h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <button
                            type="button"
                            onClick={() => setClientInfoQuestionIndex((previous) => Math.max(previous - 1, 0))}
                            disabled={clientInfoQuestionIndex === 0}
                            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Précédent
                          </button>
                          <div className="flex justify-center gap-1.5">
                            {activeItems.map((item, index) => (
                              <button
                                key={`${item.section_key}-${item.item_order}`}
                                type="button"
                                onClick={() => setClientInfoQuestionIndex(index)}
                                className={`h-2.5 rounded-full transition-all ${index === clientInfoQuestionIndex ? 'w-8 bg-blue-600' : item.checked ? 'w-2.5 bg-emerald-400' : 'w-2.5 bg-slate-300'}`}
                                title={`Question ${index + 1}`}
                              />
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => setClientInfoQuestionIndex((previous) => Math.min(previous + 1, activeItems.length - 1))}
                            disabled={clientInfoQuestionIndex >= activeItems.length - 1}
                            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-bold text-white shadow-sm hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Suivant
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
                        Aucune question disponible pour cette section.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-[1260px] w-full text-sm">
                      <thead className="bg-slate-900 text-left text-xs text-white">
                        <tr>
                          <th className="w-12 px-3 py-2">Fait</th>
                          <th className="w-28 px-3 py-2">Priorité</th>
                          <th className="w-28 px-3 py-2">Statut</th>
                          <th className="px-3 py-2">Élément</th>
                          <th className="w-96 px-3 py-2">Notes / preuves</th>
                          <th className="w-96 px-3 py-2">Action / responsable / échéance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {activeItems.map((item) => (
                          <tr key={`${item.section_key}-${item.item_order}-${item.element}`} className={item.checked ? 'bg-emerald-50' : 'odd:bg-white even:bg-slate-50 hover:bg-blue-50'}>
                            <td className="px-3 py-2 align-top">
                              <button type="button" onClick={() => updateItem(item, { checked: !item.checked, statut: !item.checked ? 'Fait' : 'À faire' })} className={`rounded-full p-1 ${item.checked ? 'text-emerald-600' : 'text-slate-300 hover:text-slate-500'}`}>
                                <CheckCircle2 className="h-5 w-5" />
                              </button>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <select value={item.priorite || ''} onChange={(e) => updateItem(item, { priorite: e.target.value as ClientOnboardingItem['priorite'] })} className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                                <option value="">-</option>
                                {onboardingPriorities.map((priority) => (
                                  <option key={priority} value={priority}>
                                    {priority}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <select value={item.statut || ''} onChange={(e) => updateItem(item, { statut: e.target.value as ClientOnboardingItem['statut'] })} className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                                <option value="">-</option>
                                {onboardingStatuses.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 align-top font-medium text-slate-800">{item.element}</td>
                            <td className="px-3 py-2 align-top">
                              <textarea value={item.notes || ''} onChange={(e) => updateItem(item, { notes: e.target.value })} className="h-32 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                            </td>
                            <td className="space-y-1 px-3 py-2 align-top">
                              <textarea value={item.action || ''} onChange={(e) => updateItem(item, { action: e.target.value })} className="h-24 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                              <div className="grid grid-cols-2 gap-2">
                                <input value={item.responsable || ''} onChange={(e) => updateItem(item, { responsable: e.target.value })} placeholder="Responsable" className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                                <input type="date" value={item.echeance || ''} onChange={(e) => updateItem(item, { echeance: e.target.value || null })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-extrabold text-slate-900">Compte rendu professionnel</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {[
                ['resume_executif', 'Résumé exécutif'],
                ['points_valides', 'Points validés'],
                ['points_a_corriger', 'Points à corriger'],
                ['recommandations_prioritaires', 'Recommandations prioritaires'],
                ['prochaines_etapes', 'Prochaines étapes'],
              ].map(([key, label]) => (
                <label key={key} className="text-xs font-semibold text-slate-600">
                  {label}
                  <textarea
                    value={selected.compte_rendu?.[key as keyof NonNullable<ClientOnboarding['compte_rendu']>] || ''}
                    onChange={(e) => updateReportField(key as keyof NonNullable<ClientOnboarding['compte_rendu']>, e.target.value)}
                    className="mt-1 h-36 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                </label>
              ))}
            </div>
            </div>

            {selected.id && (
              <div className="mt-5 rounded-2xl border border-violet-100 bg-linear-to-br from-violet-50 via-white to-cyan-50 p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-bold text-slate-900">
                    <History className="h-4 w-4" />
                    Historique
                  </div>
                  {isArchivedView ? (
                    <button type="button" onClick={handleRestore} disabled={saving} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-white px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">
                      <RotateCcw className="h-3.5 w-3.5" />
                      Restaurer
                    </button>
                  ) : (
                    <button type="button" onClick={handleDelete} disabled={saving} className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-white px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50">
                      <Archive className="h-3.5 w-3.5" />
                      Archiver
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {(selected.history || []).slice(0, 10).map((event) => (
                    <div key={event.id} className="rounded-lg bg-white px-3 py-2 text-xs text-slate-600">
                      <span className="font-semibold text-slate-900">{historyEventLabel(event.event_type)}</span>
                      {' · '}
                      {formatDate(event.created_at)}
                      {event.actor_email ? ` · ${event.actor_email}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
        ) : (
          <main className="min-h-0 flex-1 overflow-y-auto bg-slate-100 p-5">
            <section className="space-y-5">
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold uppercase text-blue-800">
                      <Lightbulb className="h-3.5 w-3.5" />
                      Esprit de la tuile
                    </div>
                    <h3 className="mt-3 text-2xl font-black text-slate-950">
                      Transformer une prise en charge en démarche maîtrisée.
                    </h3>
                    <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-600">
                      Cette tuile sert à structurer les démarrages clients TI: comprendre le contexte, valider le terrain,
                      documenter les preuves, identifier les risques et produire un compte rendu clair pour la suite.
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-950 px-4 py-3 text-sm font-bold text-white">
                    Client · Tech Prime · Checklist · Historique
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                    <Target className="h-5 w-5" />
                  </div>
                  <h4 className="mt-4 font-black text-slate-950">1. Cadrer le client</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Choisir le client PRIsME, le Tech Prime, le statut, la priorité, le site principal, le contact et la
                    période couverte pour donner un contexte fiable à toute l’équipe.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <Workflow className="h-5 w-5" />
                  </div>
                  <h4 className="mt-4 font-black text-slate-950">2. Avancer la checklist</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Parcourir les sections une question à la fois, noter les preuves, assigner les actions, fixer les
                    échéances et faire monter l’avancement sans perdre les détails terrain.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                    <History className="h-5 w-5" />
                  </div>
                  <h4 className="mt-4 font-black text-slate-950">3. Conserver la trace</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Le compte rendu, l’export PDF/Excel, les archives et l’historique gardent la mémoire de ce qui a été
                    validé, corrigé, recommandé et transféré vers les prochaines étapes.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <h4 className="font-black text-slate-950">Règle simple</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Si une information influence la stabilité, la relation client, la livraison ou une décision interne,
                  elle doit être capturée ici plutôt que rester dans une conversation éparpillée.
                </p>
              </div>
            </section>
          </main>
        )}
      </div>
    </div>
  );
}
